import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, Platform, useWindowDimensions, ActivityIndicator, RefreshControl } from 'react-native';
import { FlashList, MasonryFlashList } from '@shopify/flash-list';
import { useTheme } from '@/context/ThemeContext';
import ExploreCard from './ExploreCard';
import ExploreEmptyState from './ExploreEmptyState';
import Text from '@/components/Typography/Text';
import {
  EXPLORE_MASONRY_CARD_GAP as CARD_GAP,
  getExploreItemAspectRatio,
  getExploreMasonryColumnCount,
  getExploreMasonryColumnWidth,
  getExploreListFeedItemHeight,
} from './exploreMasonryLayout';

const LIST_FEED_ROW_GAP = 12;

const isWeb = Platform.OS === 'web';
const isNative = Platform.OS === 'ios' || Platform.OS === 'android';

// When the Explore feed is hosted inside a parent ScrollView (the default
// page layout — hero + toolbar + grid scroll together), the grid can't
// virtualize because the parent has the scroll. Mounting all items at once
// blocks the JS/UI thread on Android long enough to trigger ANRs.
//
// Progressively reveal items in chunks so the main thread stays responsive:
// the first paint covers two screens' worth of cards, then small batches are
// scheduled via `setTimeout(0)` so the JS thread yields between mounts (and
// keeps adding cards even while the user is actively scrolling, unlike
// `InteractionManager` which waits for interactions to settle).
const PROGRESSIVE_RENDER_INITIAL = 30;
const PROGRESSIVE_RENDER_BATCH = 30;
const PROGRESSIVE_RENDER_DELAY_MS = 16;

function renderListHeader(ListHeaderComponent) {
  if (!ListHeaderComponent) return null;
  if (React.isValidElement(ListHeaderComponent)) return ListHeaderComponent;
  const Cmp = ListHeaderComponent;
  return <Cmp />;
}

/**
 * Static (non-virtualized) grid/list that progressively reveals items.
 *
 * The parent ScrollView owns scrolling, so we can't use FlashList here. Instead
 * we mount items in batches scheduled by `InteractionManager`, which yields
 * between batches so the JS/UI thread stays responsive and Android doesn't ANR
 * while the Explore feed unspools.
 */
const ProgressiveStaticFeed = React.memo(function ProgressiveStaticFeed({
  items,
  isListFeed,
  columnWidth,
  numColumns,
  onItemPress,
  focusedIndex,
  ListHeaderComponent,
  ListFooterComponent,
}) {
  const total = items.length;
  // On web React render is cheap enough to mount everything immediately —
  // throttling there would just hide visible content for no reason.
  const initialCount = isWeb ? total : Math.min(PROGRESSIVE_RENDER_INITIAL, total);
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const pendingHandle = useRef(null);

  useEffect(() => {
    setVisibleCount((prev) => {
      if (isWeb) return total;
      if (total === 0) return 0;
      // When a new page lands, keep what we already rendered to avoid jarring
      // re-mounts, but never exceed the total available items.
      if (prev === 0) return Math.min(PROGRESSIVE_RENDER_INITIAL, total);
      return Math.min(Math.max(prev, PROGRESSIVE_RENDER_INITIAL), total);
    });
  }, [total]);

  useEffect(() => {
    if (isWeb) return undefined;
    if (visibleCount >= total) return undefined;
    if (pendingHandle.current) return undefined;
    pendingHandle.current = setTimeout(() => {
      pendingHandle.current = null;
      setVisibleCount((prev) => Math.min(prev + PROGRESSIVE_RENDER_BATCH, total));
    }, PROGRESSIVE_RENDER_DELAY_MS);
    return () => {
      if (pendingHandle.current != null) {
        clearTimeout(pendingHandle.current);
        pendingHandle.current = null;
      }
    };
  }, [visibleCount, total]);

  const visibleItems = useMemo(
    () => (visibleCount >= total ? items : items.slice(0, visibleCount)),
    [items, visibleCount, total],
  );

  return (
    <>
      {renderListHeader(ListHeaderComponent)}
      {isListFeed ? (
        <View style={styles.listFeedContent}>
          {visibleItems.map((item, index) => (
            <View
              key={item.id}
              style={[styles.listFeedCell, { width: columnWidth, marginBottom: LIST_FEED_ROW_GAP }]}
            >
              <ExploreCard
                item={item}
                onPress={onItemPress}
                isFocused={focusedIndex === index}
                feedMode
                feedWidth={columnWidth}
              />
            </View>
          ))}
        </View>
      ) : (
        <View
          style={[
            styles.staticGrid,
            isWeb
              ? { display: 'block', columnCount: numColumns, columnGap: CARD_GAP }
              : styles.staticGridNative,
          ]}
        >
          {visibleItems.map((item, index) => (
            <View
              key={item.id}
              style={isWeb ? styles.staticGridItemWeb : { width: columnWidth, marginBottom: CARD_GAP }}
            >
              <ExploreCard
                item={item}
                onPress={onItemPress}
                isFocused={focusedIndex === index}
              />
            </View>
          ))}
        </View>
      )}
      {ListFooterComponent ? <ListFooterComponent /> : null}
    </>
  );
});

const ExploreFeed = React.memo(({
  items,
  isLoading,
  hasMore,
  error,
  onLoadMore,
  onItemPress,
  ListHeaderComponent,
  onRefresh,
  focusedIndex,
  /** When set (e.g. catalog inside `CatalogBrowseShell`), masonry column math uses this width instead of the full window. */
  layoutWidth,
  /** `'grid'` (default) or `'list'` — list forces a single masonry column for a denser vertical scan. */
  viewMode = 'grid',
  /**
   * When true, the list does not scroll itself — a parent `ScrollView` drives vertical scroll
   * (whole page scroll). Used by explore with `CatalogDiscoveryHero` + `CatalogBrowseShell`.
   */
  parentHandlesScroll = false,
  filterFavorites = false,
  onBrowseExplore,
}) => {
  const { colors } = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const [refreshing, setRefreshing] = useState(false);

  const scrollWidth = layoutWidth ?? windowWidth;

  const numColumns = useMemo(
    () => getExploreMasonryColumnCount(scrollWidth, viewMode),
    [scrollWidth, viewMode],
  );

  const columnWidth = useMemo(
    () => getExploreMasonryColumnWidth(scrollWidth, numColumns, CARD_GAP, viewMode),
    [scrollWidth, numColumns, viewMode],
  );

  const isListFeed = viewMode === 'list';

  const listFeedItemHeight = useMemo(
    () => getExploreListFeedItemHeight(columnWidth, LIST_FEED_ROW_GAP),
    [columnWidth],
  );

  const renderGridItem = useCallback(({ item, index }) => (
    <ExploreCard
      item={item}
      onPress={onItemPress}
      isFocused={focusedIndex === index}
    />
  ), [onItemPress, focusedIndex]);

  const renderListFeedItem = useCallback(({ item, index }) => (
    <View style={[styles.listFeedCell, { width: columnWidth, marginBottom: LIST_FEED_ROW_GAP }]}>
      <ExploreCard
        item={item}
        onPress={onItemPress}
        isFocused={focusedIndex === index}
        feedMode
        feedWidth={columnWidth}
      />
    </View>
  ), [onItemPress, focusedIndex, columnWidth]);

  const keyExtractor = useCallback((item) => item.id, []);

  const getItemType = useCallback((item) => {
    const ar = getExploreItemAspectRatio(item, viewMode);
    if (ar > 1.2) return `${item.mediaType}-landscape`;
    if (ar < 0.8) return `${item.mediaType}-portrait`;
    return `${item.mediaType}-square`;
  }, [viewMode]);

  const overrideItemLayout = useCallback((layout, item) => {
    const ar = getExploreItemAspectRatio(item, viewMode);
    layout.size = (columnWidth / ar) + CARD_GAP;
  }, [columnWidth, viewMode]);

  const ListFooterComponent = useCallback(() => {
    if (isLoading && items.length > 0) {
      return (
        <View style={styles.footer}>
          <ActivityIndicator size="small" color={colors.accent_color_primary} />
        </View>
      );
    }
    if (!hasMore && items.length > 0) {
      return (
        <View style={styles.footer}>
          <Text size="xs" color="tertiary">
            {items.length} items
          </Text>
        </View>
      );
    }
    return null;
  }, [isLoading, hasMore, items.length, colors]);

  const handleRefresh = useCallback(async () => {
    if (!onRefresh) return;
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  }, [onRefresh]);

  const refreshControl = useMemo(() => {
    if (parentHandlesScroll || !isNative || !onRefresh) return undefined;
    return (
      <RefreshControl
        refreshing={refreshing}
        onRefresh={handleRefresh}
        tintColor={colors.text_color_secondary}
        colors={[colors.text_color_secondary]}
      />
    );
  }, [isNative, onRefresh, refreshing, handleRefresh, colors, parentHandlesScroll]);

  if (!items.length && !isLoading) {
    return (
      <>
        {renderListHeader(ListHeaderComponent)}
        <ExploreEmptyState
          isLoading={false}
          error={error}
          bodyWidth={scrollWidth}
          viewMode={viewMode}
          filterFavorites={filterFavorites}
          onBrowseExplore={onBrowseExplore}
        />
      </>
    );
  }

  if (!items.length && isLoading) {
    return (
      <>
        {renderListHeader(ListHeaderComponent)}
        <ExploreEmptyState isLoading bodyWidth={scrollWidth} viewMode={viewMode} />
      </>
    );
  }

  // Nested FlashList inside a parent ScrollView captures touch drags on cards and
  // blocks page scroll on mobile web (and some native layouts). Render a static
  // grid/list instead — same approach as LandingExploreShowcase.
  if (parentHandlesScroll) {
    return (
      <ProgressiveStaticFeed
        items={items}
        isListFeed={isListFeed}
        columnWidth={columnWidth}
        numColumns={numColumns}
        onItemPress={onItemPress}
        focusedIndex={focusedIndex}
        ListHeaderComponent={ListHeaderComponent}
        ListFooterComponent={ListFooterComponent}
      />
    );
  }

  if (isListFeed) {
    return (
      <FlashList
        data={items}
        renderItem={renderListFeedItem}
        keyExtractor={keyExtractor}
        estimatedItemSize={listFeedItemHeight}
        contentContainerStyle={styles.listFeedContent}
        ListHeaderComponent={ListHeaderComponent}
        onEndReached={parentHandlesScroll ? undefined : onLoadMore}
        onEndReachedThreshold={0.5}
        ListFooterComponent={ListFooterComponent}
        scrollEnabled={!parentHandlesScroll}
        showsVerticalScrollIndicator={false}
        refreshControl={refreshControl}
        extraData={`${focusedIndex}|${viewMode}|${layoutWidth ?? 0}|${parentHandlesScroll ? 1 : 0}|${columnWidth}`}
      />
    );
  }

  return (
    <MasonryFlashList
      data={items}
      numColumns={numColumns}
      renderItem={renderGridItem}
      keyExtractor={keyExtractor}
      getItemType={getItemType}
      overrideItemLayout={overrideItemLayout}
      estimatedItemSize={columnWidth * 1.33 + CARD_GAP}
      contentContainerStyle={styles.listContent}
      ListHeaderComponent={ListHeaderComponent}
      onEndReached={parentHandlesScroll ? undefined : onLoadMore}
      onEndReachedThreshold={0.5}
      ListFooterComponent={ListFooterComponent}
      scrollEnabled={!parentHandlesScroll}
      showsVerticalScrollIndicator={false}
      optimizeItemArrangement
      refreshControl={refreshControl}
      extraData={`${focusedIndex}|${viewMode}|${layoutWidth ?? 0}|${parentHandlesScroll ? 1 : 0}`}
    />
  );
});

const styles = StyleSheet.create({
  listContent: {
    paddingBottom: 80,
  },
  listFeedContent: {
    paddingBottom: 80,
    alignItems: 'center',
    paddingTop: 4,
    width: '100%',
  },
  listFeedCell: {
    alignSelf: 'center',
    maxWidth: '100%',
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  staticGrid: {
    width: '100%',
    paddingBottom: 80,
  },
  staticGridNative: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: 80,
  },
  staticGridItemWeb: {
    display: 'block',
    breakInside: 'avoid',
    WebkitColumnBreakInside: 'avoid',
    pageBreakInside: 'avoid',
    marginBottom: CARD_GAP,
  },
});

export default ExploreFeed;
